/* eslint-disable */
import React, { FC, useState } from 'react';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router';
import paths from '~/pages/paths';
import { Button, Space } from '~/components';
import {
  useCheckNafathStatus,
  useAcceptAgreement,
} from '~/controllers/contract';

import WizardContent from '~/layouts/WizardContent';

import { useGetApplication, useGetPayloadWithProvided, useSubmitStep } from '~/controllers/wizard';
import LeftLayout from '~/layouts/LeftLayout';
import { isReadOnly } from '~/utils/isReadOnly';
import { getDecodedJwt, isAgent } from '~/utils/getDecodedJwt';
import { KsaAuthorizedSignatory } from '~/types/ksa/KsaAuthorizedSignatory';
import ContentHeader from '~/components/ContentHeader';
import ApplicationNavigation from '~/layouts/uae/ApplicationNavigation';
import ApplicationSummary from '~/components/ApplicationSummary';
import { useIsMobile } from '~/hooks/useScreenSizes';

import { LoadingOutlined } from '@ant-design/icons';
import CurrentAuthorizedSignatory from './CurrentAuthorizedSignatory';
import SetAuthorizedSignatoryModal from './SetAuthorizedSignatoryModal';
import NafathModal, { NafathStep } from './NafathIntegration/NafathModal';
import AgentContractView from '../AgentContractView';
import { useDirection } from '~/hooks/i18nHooks';
import Review from '~/pages/ksa/review';
import { getTrackingId } from '~/utils/handleGetUtm';
import ErrorModal from '~/components/ErrorModal';
import AgreementReviewSection from './AgreementReviewSection';

const ContractForm: FC = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<NafathStep>('CONFIRMATION');
  const [showNafathStep, setShowNafathStep] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [uploadErrorVisible, setUploadErrorVisible] = useState(false);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<{ en: string; ar: string }>({
    en: '',
    ar: '',
  });
  const history = useHistory();

  const { t } = useTranslation('ksaApplication');
  const applicationQuery = useGetApplication();
  const payload = useGetPayloadWithProvided();

  const acceptAgreementMutation = useAcceptAgreement();
  const isMobile = useIsMobile();
  const submitMutation = useSubmitStep();
  const location = useLocation();
  const readOnly = isReadOnly(applicationQuery?.data);
  const selectedAuthorisedSignatory = payload?.authorizedSignatory as
    | KsaAuthorizedSignatory
    | undefined;
  const currentUserEmail = getDecodedJwt()?.email;
  const isCurrentUserAuthorizedSignatory =
    currentUserEmail &&
    selectedAuthorisedSignatory &&
    currentUserEmail === selectedAuthorisedSignatory?.email;

  const [signatoryModalOpen, setSignatoryModalOpen] = useState(
    () => !selectedAuthorisedSignatory && !isAgent()
  );

  const dir = useDirection();
  const openSignatoryModal = () => setSignatoryModalOpen(true);
  const closeSignatoryModal = () => {
    setSignatoryModalOpen(false);
    void applicationQuery.refetch();
  };

  const handleGenerateClick = () => {
    // contract generation no longer triggered on submit
  };

  // Called when user clicks Submit in AgreementReviewSection
  const handleAgreementSubmit = () => {
    (window as any).dataLayer.push({
      event: 'form_submit',
      page_ln: 'en',
      page_name: window.location.href,
      page_source: document.referrer || 'direct',
      page_section: 'Agreement',
      trackingId: getTrackingId(applicationQuery?.data),
      user_id: localStorage.getItem('sob_temp_uid'),
    });

    // TODO: call accept-agreement API first when ready:
    // acceptAgreementMutation.mutate(undefined, { onSuccess: () => { ... } });

    // Proceed directly to Nafath flow
    setOpen(true);
    setShowNafathStep(true);
    acceptAgreementMutation.mutate(undefined, {
      onSuccess: () => {
        setCurrentStep('NAFATH_NUMBER');
      },
      onError: (error: any) => {
        const code = error?.response?.data?.code;
        const description = error?.response?.data?.description || error?.message;
        const arDescription = error?.response?.data?.arDescription;

        (window as any).dataLayer.push({
          event: 'event_fielderror',
          page_ln: 'en',
          page_name: window.location.href,
          page_source: document.referrer || 'direct',
          page_section: 'Agreement',
          form_fieldname: 'contract',
          form_fielderror: code,
          user_id: localStorage.getItem('sob_temp_uid'),
        });

        if (code === 'NAFATH_ATTEMPT_COOLDOWN') {
          setCurrentStep('TEMPORARILY_LOCKED');
        } else if (code === 'NAFATH_PERMANENT_LOCK') {
          setCurrentStep('PERMANENTLY_LOCKED');
        } else {
          setShowNafathStep(false);
          setOpen(false);
          setUploadErrorMessage({
            en: description || t('Something went wrong'),
            ar: arDescription || description || t('Something went wrong'),
          });
          setUploadErrorVisible(true);
        }
      },
    });
  };



  const handleConfirm = () => {
    acceptAgreementMutation.mutate(undefined, {
      onSuccess: () => {
        setCurrentStep('NAFATH_NUMBER');
      },
      onError: (error: any) => {
        const code = error?.response?.data?.code;
        if (code === 'NAFATH_ATTEMPT_COOLDOWN') {
          setCurrentStep('TEMPORARILY_LOCKED');
        } else if (code === 'NAFATH_PERMANENT_LOCK') {
          setCurrentStep('PERMANENTLY_LOCKED');
        } else {
          setCurrentStep('SOMETHING_WRONG');
        }
      },
    });
  };

  // const handleRetry = () => {
  //   nafathRetryMutation.mutate(undefined, {
  //     onSuccess: () => {
  //       setCurrentStep('NAFATH_NUMBER');
  //     },
  //     onError: (error: any) => {
  //       const code = error?.response?.data?.code;
  //       if (code === 'NAFATH_ATTEMPT_COOLDOWN') {
  //         setCurrentStep('TEMPORARILY_LOCKED');
  //       } else if (code === 'NAFATH_PERMANENT_LOCK') {
  //         setCurrentStep('PERMANENTLY_LOCKED');
  //       } else {
  //         setCurrentStep('SOMETHING_WRONG');
  //       }
  //     },
  //   });
  // };

  const nafathStatus = useCheckNafathStatus(
    (data) => {
      if (data?.status === 'COMPLETED') {
        handleEndNafath();
        history.push(paths.home._);
      }
      if (data?.status === 'REJECTED') {
        setCurrentStep('REJECTED');
      }
      if (data?.status === 'EXPIRED') {
        setCurrentStep('SESSION_EXPIRED');
      }
    },
    currentStep,
    isEnabled
  );

  const handleEndNafath = () => {
    setOpen(false);
    setCurrentStep('CONFIRMATION');
  };

  const retryCooldown =
    currentStep === 'TEMPORARILY_LOCKED' || currentStep === 'PERMANENTLY_LOCKED'
      ? acceptAgreementMutation?.error?.response?.data?.details?.cooldownAt
      : currentStep === 'REJECTED' || currentStep === 'SESSION_EXPIRED'
        ? nafathStatus?.data?.cooldownAt
        : undefined;

  const attemptsLeft =
    currentStep === 'TEMPORARILY_LOCKED' || currentStep === 'PERMANENTLY_LOCKED'
      ? acceptAgreementMutation?.error?.response?.data?.details?.attemptsLeft
      : currentStep === 'REJECTED' || currentStep === 'SESSION_EXPIRED'
        ? nafathStatus?.data?.attemptsLeft
        : undefined;

  // @ts-ignore
  return (
    <>
      {!Object.keys(applicationQuery?.data?.payload?.authorizedSignatory || {})?.length && (
        <SetAuthorizedSignatoryModal
          handleGenerateClick={handleGenerateClick}
          visible={signatoryModalOpen}
          onClose={closeSignatoryModal}
        />
      )}
      <ErrorModal
        visible={uploadErrorVisible}
        error={{
          description: uploadErrorMessage.en,
          arDescription: uploadErrorMessage.ar,
        }}
        onClose={() => setUploadErrorVisible(false)}
      />
      <WizardContent
        left={<LeftLayout>{isAgent() ? <></> : <ApplicationNavigation activeSubStepIndex={showNafathStep ? 1 : 0} />}</LeftLayout>}
        content={
          <Container dir={dir}>
            {showNafathStep ? (
              <ContentHeader
                hideBackArrow={true}
                callSetTitle={false}
                title={t('Verify Your Identity')}
                subtitle={t('An authentication request has been sent to your Nafath mobile app.')}
                stageNumber={4}
              />
            ) : (
              <ContentHeader
                hideBackArrow={true}
                title={t('Agreement')}
                subtitle={t('Review your agreement.')}
                stageNumber={4}
              />
            )}
            {!isAgent() ? (
              <>
                {!showNafathStep && (
                  <div>
                    {/* Show spinner while app data loads or signatory not yet selected */}
                    {(applicationQuery.isLoading || !selectedAuthorisedSignatory) ? (
                      <LoadingWrapper>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
                        <LoadingText>{t('Loading your agreement...')}</LoadingText>
                      </LoadingWrapper>
                    ) : (
                      <AgreementReviewSection
                        signatoryName={selectedAuthorisedSignatory?.fullName}
                        onOpenSignatoryModal={openSignatoryModal}
                        onSubmit={handleAgreementSubmit}
                        isSubmitLoading={acceptAgreementMutation.isLoading}
                        disabled={readOnly || acceptAgreementMutation.isLoading}
                      />
                    )}
                  </div>
                )}
                {showNafathStep && (
                  <NafathModal
                    setIsEnabled={setIsEnabled}
                    open={open}
                    onClose={handleEndNafath}
                    currentStep={currentStep}
                    attemptsLeft={attemptsLeft}
                    nafathCooldown={acceptAgreementMutation?.data?.expiredAt}
                    nafathCode={acceptAgreementMutation?.data?.nafathCode}
                    retryCooldown={retryCooldown}
                    onContinue={handleConfirm}
                    loading={acceptAgreementMutation?.isLoading}
                  />
                )}
              </>
            ) : (
              // <AgentContractView />
              // @ts-ignore
              <Review />
            )}
          </Container>
        }
        right={
          <Space size={24} direction="vertical">
            {isAgent() ? <></> : <ApplicationSummary />}
          </Space>
        }
      />
    </>
  );
};
const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 24px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0px 2px 6px 0px rgba(0, 89, 150, 0.06), 0px 1px 4px 0px rgba(0, 89, 150, 0.04);
  border: 1px solid #f0f4f8;
  min-height: 180px;
  width: 100%;
`;

const LoadingText = styled.p`
  margin: 0;
  font-family: Inter, Tajawal, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #8c8c8c;
`;

const SectionContract = styled.div<any>`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  text-align: center;
  background: white;
  padding: 20px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '80%')};
  height: 80%;
  border-radius: 8px;
  min-width: 300px;

  .subTitle1 {
    color: #929297;
    text-align: center;
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 18px; /* 171.429% */
    letter-spacing: 0.14px;
  }
`;
const Parent = styled.div`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  display: flex;
  flex-direction: column;
  margin: auto;
  justify-content: center;
  align-content: center;
  text-align: center;
  align-items: center;
  width: 100%;
  height: 60vh;
  border-radius: 12px;
  background: #f5f9fc;
  padding: 16px;
`;

const Container = styled.div`
  font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
  //background: green;
  height: 100%;
  h1 {
    margin-bottom: 16px;
  }
  .SignContract-Subtitle {
    font-family: ${({ dir }) => (dir === 'rtl' ? 'Tajawal' : 'Inter')}, sans-serif;
    margin-bottom: 24px;
  }
`;

export default ContractForm;
